/**
 * OrchestrationChat component for displaying real-time orchestration events
 * Shows SSE events as a chat-like timeline
 */
import { useEffect, useRef } from 'react'
import type { OrchestrationEvent } from '@/lib/types'
import { cn } from '@/lib/utils'
import { 
  Bot, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  Wrench, 
  ArrowRight,
  RefreshCw,
  Search,
  Compass,
  FileText,
  FastForward
} from 'lucide-react'

export interface OrchestrationChatProps {
  /** Array of orchestration events to display */
  events: OrchestrationEvent[]
  /** Whether auto-scroll to bottom is enabled */
  autoScroll?: boolean
  /** Whether showing connecting state */
  isConnecting?: boolean
  /** Whether connection is established */
  isConnected?: boolean
  /** Whether in reconnecting state */
  isReconnecting?: boolean
  /** Whether orchestration is complete */
  isComplete?: boolean
  /** Whether to show timestamps */
  showTimestamps?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Format timestamp for display
 */
function formatTime(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })
}

/**
 * Get icon for event type
 */
function getEventIcon(eventType: OrchestrationEvent['eventType']) {
  switch (eventType) {
    case 'agent_started':
      return <Bot className="w-4 h-4" />
    case 'progress_update':
      return <ArrowRight className="w-4 h-4" />
    case 'tool_called':
      return <Wrench className="w-4 h-4" />
    case 'sub_agent_message':
      return <Bot className="w-4 h-4" />
    case 'complete':
      return <CheckCircle2 className="w-4 h-4" />
    case 'error':
      return <XCircle className="w-4 h-4" />
    case 'discovery_started':
      return <Search className="w-4 h-4" />
    case 'discovery_skipped':
      return <FastForward className="w-4 h-4" />
    case 'scout_iteration':
      return <Compass className="w-4 h-4" />
    case 'evidence_found':
      return <FileText className="w-4 h-4" />
    case 'discovery_complete':
      return <CheckCircle2 className="w-4 h-4" />
    case 'discovery_extended':
      return <RefreshCw className="w-4 h-4" />
    default:
      return <ArrowRight className="w-4 h-4" />
  }
}

/**
 * Get styling classes for event type
 */
function getEventStyles(eventType: OrchestrationEvent['eventType']) {
  switch (eventType) {
    case 'agent_started':
      return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200'
    case 'progress_update':
      return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-200'
    case 'tool_called':
      return 'bg-purple-50 border-purple-200 text-purple-800 dark:bg-purple-950 dark:border-purple-800 dark:text-purple-200'
    case 'sub_agent_message':
      return 'bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-950 dark:border-orange-800 dark:text-orange-200'
    case 'complete':
      return 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200'
    case 'error':
      return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200'
    case 'discovery_started':
      return 'bg-cyan-50 border-cyan-200 text-cyan-800 dark:bg-cyan-950 dark:border-cyan-800 dark:text-cyan-200'
    case 'discovery_skipped':
      return 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-200'
    case 'scout_iteration':
      return 'bg-teal-50 border-teal-200 text-teal-800 dark:bg-teal-950 dark:border-teal-800 dark:text-teal-200'
    case 'evidence_found':
      return 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950 dark:border-emerald-800 dark:text-emerald-200'
    case 'discovery_complete':
      return 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200'
    case 'discovery_extended':
      return 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200'
    default:
      return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-200'
  }
}

/**
 * Get agent name from sub_agent_message event data
 */
function getAgentName(event: OrchestrationEvent): string | null {
  if (event.eventType === 'sub_agent_message' && event.data) {
    const data = event.data as { agentName?: string }
    return data.agentName ?? null
  }
  return null
}

/**
 * Single event item
 */
function EventItem({ 
  event, 
  showTimestamp 
}: { 
  event: OrchestrationEvent
  showTimestamp: boolean 
}) {
  const agentName = getAgentName(event)
  
  return (
    <li
      data-event-type={event.eventType}
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg border',
        getEventStyles(event.eventType)
      )}
    >
      <div className="flex-shrink-0 mt-0.5">
        {getEventIcon(event.eventType)}
      </div>
      <div className="flex-1 min-w-0">
        {agentName && (
          <span className="text-xs font-semibold uppercase tracking-wide opacity-70 block mb-1">
            {agentName}
          </span>
        )}
        <p className="text-sm whitespace-pre-wrap">{event.message}</p>
        {showTimestamp && (
          <span className="text-xs opacity-60 mt-1 block">
            {formatTime(event.timestamp)}
          </span>
        )}
      </div>
    </li>
  )
}

/**
 * Empty state when no events
 */
function EmptyState({ isConnecting }: { isConnecting?: boolean }) {
  if (isConnecting) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span>Connecting...</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
      <Bot className="w-8 h-8 mb-2" />
      <p className="text-sm">Waiting for orchestration to start...</p>
    </div>
  )
}

/**
 * Connection status indicator
 */
function ConnectionStatus({ 
  isConnecting, 
  isReconnecting 
}: { 
  isConnecting?: boolean
  isReconnecting?: boolean 
}) {
  if (isReconnecting) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-950 text-yellow-800 dark:text-yellow-200 rounded-lg text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span>Reconnecting...</span>
      </div>
    )
  }

  if (isConnecting) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-950 text-blue-800 dark:text-blue-200 rounded-lg text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading...</span>
      </div>
    )
  }

  return null
}

/**
 * Chat-like display for orchestration events.
 * 
 * Features:
 * - Displays events as a timeline
 * - Different styling per event type
 * - Auto-scrolls to newest event
 * - Connection status indicators
 * - Accessible structure
 * 
 * @example
 * ```tsx
 * <OrchestrationChat
 *   events={events}
 *   isConnected={true}
 *   showTimestamps={true}
 * />
 * ```
 */
export function OrchestrationChat({
  events,
  autoScroll = true,
  isConnecting = false,
  isReconnecting = false,
  showTimestamps = true,
  className
}: OrchestrationChatProps) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // Auto-scroll to bottom when events change
  useEffect(() => {
    if (autoScroll && bottomRef.current && events.length > 0) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [events, autoScroll])

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Connection status */}
      <ConnectionStatus isConnecting={isConnecting} isReconnecting={isReconnecting} />
      
      {/* Events list or empty state */}
      {events.length === 0 ? (
        <EmptyState isConnecting={isConnecting} />
      ) : (
        <ul
          ref={listRef}
          role="list"
          aria-live="polite"
          aria-atomic="false"
          className="space-y-2"
        >
          {events.map((event, index) => (
            <EventItem
              key={`${event.timestamp}-${index}`}
              event={event}
              showTimestamp={showTimestamps}
            />
          ))}
        </ul>
      )}
      
      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </div>
  )
}
